import {
  context,
  trace,
  TraceFlags,
  SpanStatusCode,
  propagation,
} from '@opentelemetry/api';
import type { Span, Tracer } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ExportResultCode,
  W3CTraceContextPropagator,
  CompositePropagator,
} from '@opentelemetry/core';
import type { ExportResult } from '@opentelemetry/core';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  ParentBasedSampler,
  SimpleSpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import type {
  ReadableSpan,
  SpanExporter,
  SpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import {
  WebTracerProvider,
  StackContextManager,
} from '@opentelemetry/sdk-trace-web';
import {
  registerInstrumentations,
  InstrumentationBase,
} from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import Raven from 'raven-js';
import enviro from 'enviro';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { OtlpExporter, LOCAL_OTLP_ENDPOINT } from './otlpExporter';
import type { HttpClient } from './otlpExporter';
import { HsTraceIdRatioBasedSampler } from './HsTraceIdRatioBasedSampler';
import { instrumentQuickFetch } from '../integrations/quickfetch';

const LOCAL_OTLP_FLAG = 'OTEL_LOCAL_EXPORTER';
const PUBLIC_TELEMETRY_PATH = '/hs-open-telemetry-public/v1/public';

// https://philipwalton.com/articles/idle-until-urgent/
const isSafari = () =>
  !!(
    typeof (window as any).safari === 'object' &&
    (window as any).safari.pushNotification
  );

function getPublicBaseUrl(hublet: string): string {
  const apiUrl = getFullUrl('api');
  // getFullUrl('api') returns 'https://api.hubspot.com' for na1 but
  // 'https://api-eu1.hubspot.com' for other hublets, so use a regex
  // that strips the entire 'api(-hublet)?' subdomain.
  return apiUrl.replace(/\/\/api(?:-[^.]+)?\./, `//metrics-fe-${hublet}.`);
}

async function fetchPublicSampleRate(
  serviceName: string
): Promise<number | null> {
  const url = `${getPublicBaseUrl(
    na1
  )}${PUBLIC_TELEMETRY_PATH}/sample-ratios?serviceName=${encodeURIComponent(
    serviceName
  )}&hublet=${encodeURIComponent(
    enviro.deployed() ? enviro.getHublet() ?? na1 : na1
  )}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = (await res.json()) as { sampleRatio?: number };
  return data.sampleRatio ?? null;
}

class NoopSpanExporter implements SpanExporter {
  export(
    _spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void
  ): void {
    resultCallback({ code: ExportResultCode.SUCCESS });
  }
  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

interface OtelFeState {
  provider: WebTracerProvider | null;
  initializing: boolean;
  serviceName: string | null;
  isSampled: boolean | null;
  exportResults: ExportResult[];
  exportFailureReported: boolean;
  unloadListener: (() => void) | null;
  listenTo: string | null;
}

const GLOBAL_STATE_KEY = '__otelFe_1';
const _state: OtelFeState = ((window as unknown as Record<string, OtelFeState>)[
  GLOBAL_STATE_KEY
] ??= {
  provider: null,
  initializing: false,
  serviceName: null,
  isSampled: null,
  exportResults: [],
  exportFailureReported: false,
  unloadListener: null,
  listenTo: null,
});

let _testExporter: SpanExporter | null = null;
const _readyCallbacks: Array<() => void> = [];

export interface TracingOptions {
  enabled?: boolean;
  sampleRate?: number;
  /**
   * When `true` (the default), fetches the sample ratio for this service from the
   * public sample-ratios endpoint before initializing. The fetched value overrides
   * `sampleRate`. Falls back to `sampleRate` (default `0.01`) if the fetch fails
   * or returns no result.
   *
   * If you `await` the returned `Promise`, the provider is fully set up before any
   * spans are created. If you fire-and-forget (`void initTracing(...)`), any spans
   * created before the fetch completes will be silently dropped (no-ops). Pass
   * `false` to opt out of the fetch and use `sampleRate` directly.
   */
  dynamicSampleRate?: boolean;
  instrumentFetch?: boolean;
  instrumentXhr?: boolean;
  instrumentQuickFetch?: boolean;
  propagateTraceHeaderCorsUrls?: (string | RegExp)[];
  ignoreUrls?: (string | RegExp)[];
  applyCustomAttributesOnSpan?: (span: Span, xhr: XMLHttpRequest) => void;
  httpClient?: HttpClient;
  endpoint?: string;
  additionalSpanProcessors?: SpanProcessor[];
}

export async function initTracing(
  serviceName: string,
  {
    enabled,
    sampleRate = 0.01,
    dynamicSampleRate = true,
    instrumentFetch = false,
    instrumentXhr = false,
    instrumentQuickFetch: instrumentQuickFetchOption = false,
    propagateTraceHeaderCorsUrls,
    ignoreUrls,
    applyCustomAttributesOnSpan,
    httpClient,
    endpoint,
    additionalSpanProcessors,
  }: TracingOptions = {}
): Promise<void> {
  if (_state.provider !== null || _state.initializing) {
    console.warn(
      `[opentelemetry-fe] initTracing() was already called. Ignoring duplicate call with serviceName "${serviceName}".`
    );
    return;
  }
  _state.initializing = true;

  if (instrumentQuickFetchOption) {
    instrumentQuickFetch();
  }

  try {
    const isEnabled = enabled ?? true;

    if (dynamicSampleRate && isEnabled) {
      try {
        const fetched = await fetchPublicSampleRate(serviceName);
        if (fetched !== null) {
          sampleRate = fetched;
        } else {
          console.warn(
            `[opentelemetry-fe] No sample ratio returned for "${serviceName}"; using default ${sampleRate}.`
          );
        }
      } catch (err) {
        console.warn(
          `[opentelemetry-fe] Failed to fetch sample rate for "${serviceName}"; using default ${sampleRate}. Error: ${String(
            err
          )}`
        );
      }
    }

    if (applyCustomAttributesOnSpan && !instrumentXhr) {
      console.warn(
        '[opentelemetry-fe] applyCustomAttributesOnSpan was provided but instrumentXhr is not enabled; the hook will not be called.'
      );
    }

    let exporter: SpanExporter;
    let exporterUrl: string | null = null;
    if (!isEnabled) {
      exporter = new NoopSpanExporter();
    } else {
      let flag: string | null = null;
      try {
        flag = localStorage.getItem(LOCAL_OTLP_FLAG);
      } catch (_e) {
        // localStorage may be unavailable (e.g. private browsing with strict settings)
      }

      if (flag === 'otlp') {
        exporterUrl = LOCAL_OTLP_ENDPOINT;
        exporter = new OtlpExporter();
      } else if (flag === 'tempo' || enviro.deployed()) {
        const resolvedEndpoint =
          endpoint ??
          `${getPublicBaseUrl(
            enviro.deployed() ? enviro.getHublet() ?? na1 : na1
          )}${PUBLIC_TELEMETRY_PATH}/traces`;

        exporterUrl = resolvedEndpoint;
        exporter = new OtlpExporter(resolvedEndpoint, {
          httpClient,
        });
      } else if (flag === 'console') {
        exporter = new ConsoleSpanExporter();
      } else {
        if (flag !== null) {
          console.warn(
            `[opentelemetry-fe] Unrecognized OTEL_LOCAL_EXPORTER value: "${flag}". No spans will be exported.`
          );
        }
        exporter = new NoopSpanExporter();
      }
    }

    const allIgnoreUrls: (string | RegExp)[] = [
      ...(exporterUrl
        ? [new RegExp(exporterUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))]
        : []),
      ...(ignoreUrls ?? []),
    ];

    const trackedExporter: SpanExporter = {
      export(
        spans: ReadableSpan[],
        resultCallback: (result: ExportResult) => void
      ): void {
        exporter.export(spans, (result: ExportResult) => {
          _state.exportResults.push(result);
          if (
            result.code === ExportResultCode.FAILED &&
            !_state.exportFailureReported
          ) {
            _state.exportFailureReported = true;
            Raven.captureMessage('[opentelemetry-fe] Span export failed', {
              level: 'info',
              extra: { error: result.error?.message },
              tags: { library_name: 'opentelemetry-fe' },
            });
          }
          resultCallback(result);
        });
      },
      shutdown(): Promise<void> {
        return exporter.shutdown();
      },
    };

    const spanProcessor = _testExporter
      ? new SimpleSpanProcessor(_testExporter)
      : new BatchSpanProcessor(trackedExporter);

    const validatedSampleRate = (() => {
      if (Number.isNaN(sampleRate)) {
        console.warn(
          '[opentelemetry-fe] sampleRate is NaN; falling back to default (0.01).'
        );
        return 0.01;
      }
      if (sampleRate < 0 || sampleRate > 1) {
        const clamped = Math.min(1, Math.max(0, sampleRate));
        console.warn(
          `[opentelemetry-fe] sampleRate ${sampleRate} is out of range [0.0, 1.0]; clamping to ${clamped}.`
        );
        return clamped;
      }
      return sampleRate;
    })();

    const sampler = new ParentBasedSampler({
      root: new HsTraceIdRatioBasedSampler(validatedSampleRate),
    });

    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({ [ATTR_SERVICE_NAME]: serviceName }),
      sampler,
      spanProcessors: [spanProcessor, ...(additionalSpanProcessors ?? [])],
    });

    const contextManager = new StackContextManager();
    contextManager.enable();

    const shouldInstrument = instrumentFetch || instrumentXhr;

    provider.register({
      contextManager,
      propagator: isEnabled
        ? new W3CTraceContextPropagator()
        : new CompositePropagator(),
    });

    if (shouldInstrument) {
      const instrumentations: InstrumentationBase[] = [];
      if (instrumentFetch) {
        instrumentations.push(
          new FetchInstrumentation({
            propagateTraceHeaderCorsUrls,
            ignoreUrls: allIgnoreUrls,
          })
        );
      }
      if (instrumentXhr) {
        instrumentations.push(
          new XMLHttpRequestInstrumentation({
            propagateTraceHeaderCorsUrls,
            ignoreUrls: allIgnoreUrls,
            applyCustomAttributesOnSpan,
          })
        );
      }
      registerInstrumentations({
        instrumentations,
        tracerProvider: provider,
      });
    }

    const probeSpan = trace.getTracer(serviceName).startSpan('otel-init');
    _state.isSampled =
      probeSpan.spanContext().traceFlags === TraceFlags.SAMPLED;
    probeSpan.end();

    _state.provider = provider;
    _state.serviceName = serviceName;

    for (const cb of _readyCallbacks.splice(0)) {
      cb();
    }

    const listenTo = isSafari() ? 'beforeunload' : 'visibilitychange';
    const flushOnUnload = () => {
      if (
        document.visibilityState === 'hidden' ||
        listenTo === 'beforeunload'
      ) {
        void provider.forceFlush();
        if (listenTo === 'beforeunload') {
          try {
            window.removeEventListener(listenTo, flushOnUnload, true);
          } catch (_e) {
            // ignore
          }
          _state.unloadListener = null;
        }
      }
    };
    _state.listenTo = listenTo;
    _state.unloadListener = flushOnUnload;
    try {
      window.addEventListener(listenTo, flushOnUnload, true);
    } catch (_e) {
      // ignore
    }
  } finally {
    _state.initializing = false;
  }
}

export function isSampled(): boolean | null {
  return _state.isSampled;
}

export function isTracingInitializing(): boolean {
  return _state.initializing;
}

export function onTracingReady(callback: () => void): void {
  if (_state.provider !== null) {
    callback();
  } else {
    _readyCallbacks.push(callback);
  }
}

export function getTracer(name?: string): Tracer {
  if (_state.provider === null && !_state.initializing) {
    throw new Error(
      'OpenTelemetry tracing has not been initialized. Call initTracing(serviceName) before using getTracer(). Use getSafeTracer() if you cannot guarantee initTracing() has been called.'
    );
  }
  return getSafeTracer(name);
}

/**
 * Like getTracer(), but never throws. Returns a no-op tracer if tracing has
 * not been initialized (or is still initializing) instead of raising an
 * error. Intended for library code that emits spans but can't control or
 * verify whether the host app has called initTracing().
 */
export function getSafeTracer(name?: string): Tracer {
  if (_state.provider !== null && _state.serviceName !== null) {
    return trace.getTracer(name ?? _state.serviceName);
  }
  // Tracing is either still initializing (e.g. awaiting the dynamic
  // sample-rate fetch) or was never initialized. Either way, no global OTel
  // provider is registered, so trace.getTracer() returns a no-op tracer —
  // spans created from it are silently dropped rather than throwing.
  return trace.getTracer(name ?? 'opentelemetry-fe-pending');
}

export async function flushTracing(): Promise<{
  success: boolean;
  error?: string;
}> {
  if (_state.provider === null) {
    throw new Error(
      'OpenTelemetry tracing has not been initialized. Call initTracing(serviceName) before using flushTracing().'
    );
  }
  _state.exportResults = [];
  await _state.provider.forceFlush();
  if (_state.exportResults.length === 0) {
    return { success: false, error: 'No spans were exported' };
  }
  const failed = _state.exportResults.find(
    (r) => r.code === ExportResultCode.FAILED
  );
  return {
    success: !failed,
    error: failed?.error?.message,
  };
}

export async function _initTracingForTesting(
  serviceName: string,
  options: TracingOptions,
  exporter: SpanExporter
): Promise<void> {
  _testExporter = exporter;
  await initTracing(serviceName, options);
}

export function _setTestExporterForTesting(exporter: SpanExporter): void {
  _testExporter = exporter;
}

export function _resetForTesting(): void {
  if (_state.provider !== null) {
    void _state.provider.shutdown();
  }
  if (_state.unloadListener !== null && _state.listenTo !== null) {
    try {
      window.removeEventListener(_state.listenTo, _state.unloadListener, true);
    } catch (_e) {
      // ignore
    }
  }
  propagation.disable();
  context.disable();
  trace.disable();
  delete (window as unknown as Record<string, unknown>)[GLOBAL_STATE_KEY];
  _state.provider = null;
  _state.initializing = false;
  _state.serviceName = null;
  _state.isSampled = null;
  _state.exportResults = [];
  _state.exportFailureReported = false;
  _state.unloadListener = null;
  _state.listenTo = null;
  _testExporter = null;
  _readyCallbacks.length = 0;
}

export { context, trace, propagation, SpanStatusCode };
export type { Span, Tracer };
